import {RatingStars} from '@frontend/components/RatingStars'
import {trans} from '@frontend/helpers'
import {MyReviewsApi} from '@frontend/modules/private/reviews/MyReviewsApi'
import {useAutogrowOnTextArea} from '@shared/helpers/useAutogrowOnTextArea'
import type {UserReviewType} from '@shared/types/UserReviews'
import {
    type ChangeEvent,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalFooterCloseButton} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooterCloseButton'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'

interface Props {
    chatId: number | null
    reviewType: UserReviewType
    show: boolean | null
    onClose: () => void
    onClosed?: () => void
    onReviewAdded: () => void
}

// Модальное окно отправки отзыва о продавце или исполнителе услуг.
export function AddReviewModal(props: Props) {

    const {
        chatId,
        reviewType,
        show,
        onClose,
        onClosed,
        onReviewAdded,
    } = props

    // Оценка.
    const [
        rating,
        setRating,
    ] = useState<number>(0)
    // Текст отзыва.
    const [
        reviewText,
        setReviewText,
    ] = useState<string>('')
    // Состояние отправки сообщения.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Работа с авто-высотой текстового поля ввода.
    const {
        inputRefHandler,
    } = useAutogrowOnTextArea({
        value: reviewText,
    })

    // Отправить отзыв.
    const sendReview = (): void => {
        if (
            isProcessing
            || rating === 0
            || !chatId
        ) {
            return
        }
        setIsProcessing(true)
        MyReviewsApi.addReviewFoChat(
            chatId,
            rating,
            reviewText.trim()
        )
            .then(onReviewAdded)
            .catch((error: ApiError) => {
                handleErrorResponse(error, {
                    suppressValidationErrorMessage: true,
                    validationErrors(errors: AnyObject<string>): void {
                        const messages: string[] = Object.values(errors)
                        ToastService.error(
                            messages.join('; ').replace(/\.;/g, ';'),
                            messages.length > 2 ? 8000 : 5000
                        )
                    },
                })
            })
            .finally(() => {
                setIsProcessing(false)
            })
    }

    let texts: {header: string; rating: string;}
    switch (reviewType) {
        case 'executor':
            texts = {
                header: trans().cabinet.user_reviews.add_modal.header_for_executor,
                rating: trans().cabinet.user_reviews.add_modal.rating_for_executor,
            }
            break
        case 'service_requester':
            texts = {
                header: trans().cabinet.user_reviews.add_modal.header_for_service_requester,
                rating: trans().cabinet.user_reviews.add_modal.rating_for_service_requester,
            }
            break
        default:
            texts = {
                header: trans().cabinet.user_reviews.add_modal.header_for_seller,
                rating: trans().cabinet.user_reviews.add_modal.rating_for_seller,
            }
            break
    }

    return (
        <Modal
            show={show && !!chatId}
            onClose={onClose}
            onClosed={() => {
                onClosed?.()
                setRating(0)
                setReviewText('')
            }}
            size="md"
            staticBackdrop
            scrollable
            closeOnEsc
        >
            <ModalContent>
                <ModalHeader
                    title={texts.header}
                    onClose={onClose}
                    border={false}
                />
                <ModalBody>
                    <form
                        className="m-0 flex-1"
                        onSubmit={event => {
                            event.preventDefault()
                            sendReview()
                        }}
                    >
                        <div className="mb-3 flex-row-center-vertical">
                            <div className="me-2">
                                {texts.rating}*:
                            </div>
                            <RatingStars
                                rating={rating}
                                onClick={(value: number): void => {
                                    setRating(value)
                                }}
                            />
                        </div>
                        <Input
                            textarea
                            label={trans().cabinet.user_reviews.add_modal.review}
                            value={reviewText}
                            wrapperClassName="m-0"
                            className="autogrow"
                            style={{
                                minHeight: 110,
                                maxHeight: 290,
                            }}
                            disabled={isProcessing}
                            inputRef={inputRefHandler}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                                setReviewText(event.currentTarget.value)
                            }}
                            onBlur={() => {
                                setReviewText(reviewText.trim())
                            }}
                        />
                    </form>
                </ModalBody>
                <ModalFooter
                    flexBetween
                    border={false}
                >
                    <ModalFooterCloseButton
                        title={trans().common.modal.cancel}
                        className="d-none d-sm-block"
                        onClose={onClose}
                    />
                    <Button
                        color="green"
                        className="full-width-sm-down"
                        disabled={isProcessing || rating === 0}
                        onClick={sendReview}
                    >
                        {trans().cabinet.user_reviews.add_modal.submit}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}
